/**
 * Eye Dropper Tool - Renk Alma Aracı
 * Canvas'dan renk alır ve kullanıcıya gösterir
 * 
 * @module eyedropper
 */

/**
 * RGB renk bilgisi
 */
export interface PickedColor {
  r: number;
  g: number;
  b: number;
  a: number;
  hex: string;
  hsl: {
    h: number;
    s: number;
    l: number;
  };
}

/**
 * EyeDropper Tool API
 */
export class EyeDropperTool {
  /**
   * Canvas'tan renk al
   * @param canvas - Canvas element'i
   * @param x - X koordinatı (canvas içinde)
   * @param y - Y koordinatı (canvas içinde)
   * @returns RGB renk bilgisi
   */
  /**
   * Canvas'tan renk al
   * @param canvas - Canvas element'i veya elementleri
   * @param x - X koordinatı (canvas içinde)
   * @param y - Y koordinatı (canvas içinde)
   * @returns RGB renk bilgisi
   */
  pickColor(canvas: HTMLCanvasElement | HTMLCanvasElement[], x: number, y: number): PickedColor {
    if (Array.isArray(canvas)) {
        return this.pickColorFromStack(canvas, x, y);
    }

    // Canvas context'i al
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw new Error('Canvas context bulunamadı');
    }

    // Canvas'taki piksel değerlerini al
    const imgData = ctx.getImageData(x, y, 1, 1);
    const [r, g, b] = imgData.data.slice(0, 3);
    const a = imgData.data[3];

    // HEX kodunu hesapla
    const hex = '#' + [r, g, b].map(curr => {
      const hexVal = curr.toString(16);
      return hexVal.length === 1 ? '0' + hexVal : hexVal;
    }).join('');

    // HSL değerlerini hesapla
    const hsl = this.rgbToHsl(r, g, b);

    return {
      r,
      g,
      b,
      a: a / 255,
      hex,
      hsl
    };
  }

  /**
   * Bir dizi canvas katmanından (üstten alta doğru) renk al
   */
  private pickColorFromStack(stack: HTMLCanvasElement[], x: number, y: number): PickedColor {
      // 1x1'lik bir geçici canvas oluştur (Blending için en doğru yaklaşım)
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 1;
      tempCanvas.height = 1;
      const tCtx = tempCanvas.getContext('2d', { willReadFrequently: true })!;

      // Katmanları alttan üste doğru çiz (doğru blending için)
      // Array'daki sıralama genelde 0: alttaki, length-1: üstteki şeklindedir
      for (let i = 0; i < stack.length; i++) {
          const l = stack[i];
          if (l) tCtx.drawImage(l, x, y, 1, 1, 0, 0, 1, 1);
      }

      const imgData = tCtx.getImageData(0, 0, 1, 1);
      const [r, g, b] = imgData.data.slice(0, 3);
      const a = imgData.data[3];

      const hex = '#' + [r, g, b].map(curr => {
        const hexVal = curr.toString(16);
        return hexVal.length === 1 ? '0' + hexVal : hexVal;
      }).join('');

      return {
        r, g, b, a: a / 255, hex,
        hsl: this.rgbToHsl(r, g, b)
      };
  }

  /**
   * RGB'den HSL'e dönüştür
   * @param r - Red (0-255)
   * @param g - Green (0-255)
   * @param b - Blue (0-255)
   * @returns HSL değerleri
   */
  private rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    // RGB'yi 0-1 aralığına çevir
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    let h = 0;
    let s = 0;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }

  /**
   * Seçilen rengi layer olarak kaydet
   * @param color - Seçilen renk
   * @param canvas - Canvas element'i
   * @returns Yeni layer bilgisi
   */
  storeAsLayer(color: PickedColor, canvas: HTMLCanvasElement): { id: string; type: 'solid'; color: PickedColor } {
    return {
      id: `solid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'solid' as const,
      color: color
    };
  }

  /**
   * Renk palitresi için renk örnekleri oluştur
   * @param count - Örnek sayısı
   * @returns Renk listesi
   */
  generateColorSwatches(count: number = 9): PickedColor[] {
    const swatches: PickedColor[] = [];
    const baseHues = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

    for (let i = 0; i < count; i++) {
      const hue = baseHues[i % baseHues.length];
      const saturation = Math.floor(Math.random() * 100) + 50;
      const lightness = Math.floor(Math.random() * 50) + 25;

      const hsl = { h: hue, s: saturation, l: lightness };
      const rgb = this.hslToRgb(hsl.h, hsl.s, hsl.l);

      swatches.push({
        r: rgb.r,
        g: rgb.g,
        b: rgb.b,
        a: 1,
        hex: this.rgbToHex(rgb.r, rgb.g, rgb.b),
        hsl
      });
    }

    return swatches;
  }

  /**
   * HSL'den RGB'ye dönüştür
   */
  private hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    const r = Math.round((h < 60 ? x : h < 180 ? c : x) + m) * 255;
    const g = Math.round((h < 120 ? c : x < 180 ? x : 0) + m) * 255;
    const b = Math.round((x < 60 ? 0 : c < 180 ? x : c) + m) * 255;

    return { r, g, b };
  }

  /**
   * RGB'den HEX'e dönüştür
   */
  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  /**
   * Event listener bağla
   * @param container - Container element'i
   * @param canvas - Canvas element'i
   * @param onPick - Renk seçilince tetiklenecek callback
   */
  bindEvents(
    container: HTMLElement,
    canvas: HTMLCanvasElement,
    onPick: (color: PickedColor, x: number, y: number) => void
  ): () => void {
    let isDragging = false;
    let lastColor: PickedColor | null = null;

    // Mouse down
    canvas.addEventListener('mousedown', (e) => {
      isDragging = false;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      try {
        lastColor = this.pickColor(canvas, x, y);
        if (lastColor) {
          onPick(lastColor, x, y);
        }
      } catch (error) {
        console.error('Renk alılamadı:', error);
      }
    });

    // Mouse move (drag ile renk seçimi)
    canvas.addEventListener('mousemove', (e) => {
      if (e.buttons === 1 && lastColor) {
        isDragging = true;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        try {
          const color = this.pickColor(canvas, x, y);
          if (color) {
            onPick(color, x, y);
          }
        } catch (error) {
          console.error('Renk alınamadı:', error);
        }
      }
    });

    // Mouse up - renk seçimi sonu
    canvas.addEventListener('mouseup', () => {
      isDragging = false;
    });

    // Mouse leave
    canvas.addEventListener('mouseleave', () => {
      isDragging = false;
    });

    // Temizleyici fonksiyon döndür
    return () => {
      canvas.removeEventListener('mousedown', () => {});
      canvas.removeEventListener('mousemove', () => {});
      canvas.removeEventListener('mouseup', () => {});
      canvas.removeEventListener('mouseleave', () => {});
    };
  }

  /**
   * Renk seçim modal'ını oluştur
   * @returns Modal HTML
   */
  createColorPickerModal(): { container: HTMLElement; close: () => void; hexDisplay: HTMLDivElement; colorPreview: HTMLDivElement; swatches: HTMLDivElement } {
    const modal = document.createElement('div');
    modal.className = 'eyedropper-modal';
    modal.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(4px);
      border-radius: 12px;
      padding: 20px;
      min-width: 320px;
      max-width: 400px;
      z-index: 10000;
      color: #fff;
      font-family: system-ui, -apple-system, sans-serif;
    `;

    const hexDisplay = document.createElement('div');
    hexDisplay.style.cssText = `
      text-align: center;
      margin-bottom: 10px;
      font-size: 14px;
      font-weight: bold;
      padding: 10px;
      border-radius: 8px;
      min-height: 40px;
      border: 2px solid #444;
    `;
    modal.appendChild(hexDisplay);

    const colorPreview = document.createElement('div');
    colorPreview.style.cssText = `
      width: 100%;
      height: 80px;
      margin-bottom: 15px;
      border-radius: 8px;
      border: 2px solid #444;
      background: #fff;
    `;
    modal.appendChild(colorPreview);

    const swatches = document.createElement('div');
    swatches.className = 'eyedropper-swatches';
    swatches.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 15px;
      min-height: 80px;
    `;
    modal.appendChild(swatches);

    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Tamam';
    closeBtn.style.cssText = `
      width: 100%;
      padding: 10px;
      background: #4a4a4a;
      color: #fff;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
    `;
    closeBtn.addEventListener('click', () => {
      this.removeModal(modal);
    });
    modal.appendChild(closeBtn);

    document.body.appendChild(modal);

    return {
      container: modal,
      close: () => this.removeModal(modal),
      hexDisplay,
      colorPreview,
      swatches
    };
  }

  /**
   * Modal'ı kaldır
   */
  private removeModal(modal: HTMLElement): void {
    if (modal.parentElement) {
      modal.parentElement.removeChild(modal);
    }
  }

  /**
   * Renk modal'ına yeni renk ekle
   * @param container - Modal container
   * @param color - Seçilen renk
   */
  addColorToModal(
    container: HTMLElement,
    color: PickedColor,
    onApply: (c: PickedColor) => void,
    onCancel: () => void
  ): void {
    // Renk örneği oluştur
    const swatch = document.createElement('div');
    swatch.className = 'eyedropper-swatch';
    swatch.style.cssText = `
      width: 40px;
      height: 40px;
      border-radius: 6px;
      cursor: pointer;
      border: 2px solid #333;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      transition: transform 0.1s;
    `;
    swatch.style.background = `rgb(${color.r}, ${color.g}, ${color.b})`;

    const hexText = document.createElement('div');
    hexText.style.cssText = `
      font-size: 10px;
      text-align: center;
      margin-top: 4px;
      color: #888;
    `;
    hexText.textContent = color.hex.toUpperCase();

    swatch.appendChild(hexText);

    swatch.addEventListener('click', () => {
      onApply(color);
    });

    container.appendChild(swatch);
  }
}

export default EyeDropperTool;